import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SalesList from '../components/sales/SalesList';
import SaleForm from '../components/sales/SaleForm';
import { supabase, db } from '../lib/supabase';
import type { Sale } from '../types/sale';

const Sales: React.FC = () => {
  const [isNewSale, setIsNewSale] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const location = useLocation();

  useEffect(() => {
    const state = location.state as { openNewSale?: boolean };
    if (state?.openNewSale) {
      setIsNewSale(true);
      // Clear location state to prevent reopening form on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadSales();
  }, []);

  const loadSales = async () => {
    try {
      // First, fetch sales data
      const { data: salesData, error: salesError } = await supabase
        .from('sales')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (salesError) throw salesError;
      if (!salesData) return;

      // Create a Set of unique customer IDs
      const customerIds = new Set(salesData.map(sale => sale.customer_id).filter(Boolean));

      // If there are customer IDs, fetch their data
      let customersMap: Record<string, any> = {};
      
      if (customerIds.size > 0) {
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('*')
          .in('id', Array.from(customerIds));

        if (customersError) throw customersError;
        
        if (customersData) {
          customersMap = customersData.reduce((acc, customer) => {
            acc[customer.id] = customer;
            return acc;
          }, {} as Record<string, any>);
        }
      }

      // Merge sales with customer data
      const salesWithCustomers = salesData.map(sale => ({
        ...sale,
        customer: sale.customer_id ? customersMap[sale.customer_id] : null
      }));

      setSales(salesWithCustomers);
    } catch (error) {
      console.error('Error loading sales:', error);
    }
  };

  const handleSaleSubmit = async (data: Sale) => {
    try {
      const { data: newSale, error } = await supabase
        .from('sales')
        .insert([{
          customer_id: data.customer?.id,
          total: data.total,
          discount: data.discount,
          payment_method: data.paymentMethod,
          status: data.status,
          notes: data.notes
        }])
        .select()
        .single();

      if (error) throw error;

      if (newSale) {
        // Insert sale items
        const saleItems = data.items.map(item => ({
          sale_id: newSale.id,
          product_id: item.productId,
          quantity: item.quantity,
          price: item.unitPrice,
          discount: item.discount
        }));

        const { error: itemsError } = await supabase
          .from('sale_items')
          .insert(saleItems);

        if (itemsError) throw itemsError;
      }

      setIsNewSale(false);
      await loadSales();
    } catch (error) {
      console.error('Error saving sale:', error);
    }
  };

  const handleSelectSale = (sale: Sale | null) => {
    if (sale === null) {
      // This means we want to create a new sale
      setIsNewSale(true);
      setSelectedSale(null);
    } else {
      setSelectedSale(sale);
      setIsNewSale(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Gestão de Vendas
          </h1>
          <button
            onClick={() => handleSelectSale(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Nova Venda
          </button>
        </div>

        {isNewSale ? (
          <SaleForm onSubmit={handleSaleSubmit} onCancel={() => setIsNewSale(false)} />
        ) : (
          <SalesList sales={sales} onSelect={handleSelectSale} />
        )}
      </div>
    </div>
  );
};

export default Sales;